import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MultiAuthService } from '../services/multi-auth.service';
import type { AuthProvider, AuthProviderInfo } from '../services/multi-auth.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { UserCacheService } from '../services/user-cache.service';
import { AuthMockService } from '../services/auth-mock.service';

interface AuthProvidersProps {
  onSuccess?: (provider: AuthProvider) => void;
  showTitle?: boolean;
  excludeProviders?: AuthProvider[];
}

export const AuthProviders: React.FC<AuthProvidersProps> = ({
  onSuccess,
  showTitle = true,
  excludeProviders = []
}) => {
  const navigate = useNavigate();
  const [loadingProvider, setLoadingProvider] = useState<AuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const availableProviders = MultiAuthService.getAvailableProviders()
    .filter(provider => !excludeProviders.includes(provider.id));

  const handleProviderLogin = async (providerId: AuthProvider) => {
    if (providerId === 'email') return; // El email se maneja en el formulario principal
    
    setLoadingProvider(providerId);
    setError(null);

    try {
      let result;
      
      switch (providerId) {
        case 'google':
          result = await MultiAuthService.signInWithGoogle();
          break;
        case 'facebook':
          result = await MultiAuthService.signInWithFacebook();
          break;
        case 'microsoft':
          result = await MultiAuthService.signInWithMicrosoft();
          break;
        default:
          throw new Error(`Proveedor ${providerId} no soportado`);
      }

      LoggerService.info('Social login successful', 'AuthProviders', { 
        provider: providerId, 
        isNewUser: result.isNewUser 
      });

      // Para autenticación social mock, usar AuthMockService para simular login completo
      const mockAuthService = new AuthMockService();
      const mockUser = await mockAuthService.login(
        result.user.email || `mock_${providerId}@example.com`,
        'mock_password'
      );
      
      // Guardar usuario en cache
      UserCacheService.saveUser(
        mockUser.email || `mock_${providerId}@example.com`,
        mockUser.displayName || `Usuario ${providerId}`,
        undefined
      );

      // Callback opcional
      onSuccess?.(providerId);
      
      // Navegar al dashboard
      navigate('/dashboard');
      
      // Mostrar mensaje de éxito
      console.log(`✅ Login exitoso con ${providerId}:`, mockUser);
      
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión';
      setError(errorMessage);
      LoggerService.error('Social login failed', 'AuthProviders', { 
        provider: providerId, 
        error: err 
      });
    } finally {
      setLoadingProvider(null);
    }
  };

  const getProviderButton = (provider: AuthProviderInfo) => {
    const isLoading = loadingProvider === provider.id;
    const isEmail = provider.id === 'email';
    
    if (isEmail) return null; // No mostramos el botón de email aquí

    const colors = {
      google: 'bg-red-600 hover:bg-red-700 text-white',
      facebook: 'bg-blue-600 hover:bg-blue-700 text-white',
      microsoft: 'bg-blue-800 hover:bg-blue-900 text-white'
    };

    return (
      <button
        key={provider.id}
        onClick={() => handleProviderLogin(provider.id)}
        disabled={isLoading || loadingProvider !== null}
        className={`
          w-full flex items-center justify-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 
          ${colors[provider.id as keyof typeof colors] || 'bg-gray-600 hover:bg-gray-700 text-white'}
          disabled:opacity-50 disabled:cursor-not-allowed
          transform hover:scale-105 active:scale-95
        `}
      >
        {isLoading ? (
          <i className="ri-loader-4-line animate-spin text-lg"></i>
        ) : (
          <i className={`${provider.icon} text-lg`}></i>
        )}
        <span>
          {isLoading ? 'Conectando...' : `Continuar con ${provider.name}`}
        </span>
      </button>
    );
  };

  const socialProviders = availableProviders.filter(p => p.id !== 'email');

  if (socialProviders.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 mt-3 mb-3">
      {showTitle && (
        <div className="text-center">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="flex-1 h-px bg-white/20"></div>
            <span className="text-sm text-gray-300">O continúa con</span>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-400/50 text-red-300 rounded-lg backdrop-blur-sm text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {socialProviders.map(provider => getProviderButton(provider))}
      </div>

      {showTitle && (
        <div className="text-center text-xs text-gray-400 mt-4">
          Al continuar, aceptas nuestros términos de servicio y política de privacidad
        </div>
      )}
    </div>
  );
};